import { useState, useEffect } from "react";

/**
 * Script loading status enumeration
 */
enum ScriptStatus {
  IDLE = "idle",
  LOADING = "loading",
  READY = "ready",
  ERROR = "error",
}

/**
 * Custom hook to dynamically load an external script and track its status
 * @param src - URL of the script to load
 * @returns Current status of the script loading process
 */
export const useScript = (src: string): ScriptStatus => {
  const [status, setStatus] = useState<ScriptStatus>(
    src ? ScriptStatus.LOADING : ScriptStatus.IDLE
  );

  useEffect(() => {
    if (!src) {
      setStatus(ScriptStatus.IDLE);
      return;
    }

    const existingScript: HTMLScriptElement | null = document.querySelector(
      `script[src="${src}"]`
    );

    const createNewScript = (): HTMLScriptElement => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.setAttribute("data-status", ScriptStatus.LOADING);
      document.body.appendChild(script);
      return script;
    };

    const script = existingScript || createNewScript();

    if (existingScript) {
      const currentStatus =
        existingScript.getAttribute("data-status") ?? ScriptStatus.IDLE;

      setStatus(currentStatus as ScriptStatus);
    }

    const updateScriptAttribute = (event: Event): void => {
      script?.setAttribute(
        "data-status",
        event.type === "load" ? ScriptStatus.READY : ScriptStatus.ERROR
      );
    };

    const updateScriptStatus = (event: Event): void => {
      setStatus(
        event.type === "load" ? ScriptStatus.READY : ScriptStatus.ERROR
      );
    };

    script.addEventListener("load", updateScriptAttribute);
    script.addEventListener("error", updateScriptAttribute);
    script.addEventListener("load", updateScriptStatus);
    script.addEventListener("error", updateScriptStatus);

    return () => {
      script.removeEventListener("load", updateScriptStatus);
      script.removeEventListener("error", updateScriptStatus);
      script.removeEventListener("load", updateScriptAttribute);
      script.removeEventListener("error", updateScriptAttribute);
    };
  }, [src]);

  return status;
};
